import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Alert } from "../components/Alert";
import { validateEmail } from "../utils/validation";
import { authApi } from "../services/authService";

interface ForgotPasswordProps {
    initialEmail?: string;
}

interface FieldErrors {
    [key: string]: string;
}

interface Message {
  text: string;
  type: 'error' | 'success';
}

export const ForgotPassword: React.FC<ForgotPasswordProps> = ({ initialEmail }) => {
    const alertRef = useRef<HTMLDivElement>(null);

    const [email, setEmail] = useState<string>(initialEmail || '');
    const [message, setMessage] = useState<Message>({ text: '', type: 'error' });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

    const clearFieldError = (fieldName: string): void => {
        if (fieldErrors[fieldName]) {
            setFieldErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[fieldName];
                return newErrors;
            });
        }
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setEmail(e.target.value);
        clearFieldError('email');
    };

    const validateForm = (): FieldErrors => {
        const errors: FieldErrors = {};
        
        const emailError = validateEmail(email);
        if (emailError) {
            errors.email = emailError;
        }

        return errors;
    };

    const getInputClass = (fieldName: string): string => {
        return `w-full input ${fieldErrors[fieldName] ? 'input-error' : ''}`;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        setIsLoading(true);
        setMessage({ text: '', type: 'error' });
        setFieldErrors({});

        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setFieldErrors(validationErrors);
            setMessage({ text: validationErrors.email || 'Please check your input', type: 'error' });
            setIsLoading(false);
            return;
        }

        const result = await authApi('/auth/forgot-password', {
            method: 'POST',
            body: JSON.stringify({ email }),
        });

        if (result.success) {
            setMessage({ text: result.message, type: 'success' });
        } else {
            setMessage({ text: result.error || 'An error occurred. Please try again.', type: 'error' });
        }
        setIsLoading(false);
    };

    return (
        <div className="login-layout">
            <div className="lcont">
                <div ref={alertRef}>
                    {message.text && (
                        <Alert type={message.type} message={message.text} />
                    )}
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                    <fieldset className="fieldset">
                      <legend className="fieldset-legend">Email</legend>
                      <input
                          type="email"
                          placeholder="you@researchcentre.org"
                          className={getInputClass('email')}
                          value={email}
                          onChange={handleEmailChange}
                          aria-describedby="email-help"
                          autoComplete="email"
                        />
                    </fieldset>
                    <button type="submit" className="w-full btn btn-primary" disabled={isLoading}>
                        {isLoading ? <span className="loading loading-spinner"></span> : 'Send Reset Link'}
                    </button>
                </form>
                <p className="text-sm text-center">
                    Remembered your password? <Link to="/login" className="text-primary hover:underline">Login</Link>
                </p>
            </div>
        </div>
    );
};
